import './ProductActionsV2.scss';

import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import Button from '@ulta/core/components/Button/Button';
import InlineMessage from '@ulta/core/components/InlineMessage/InlineMessage';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { useIntersectionProcessor } from '@ulta/core/hooks/useIntersectionProcessor/useIntersectionProcessor';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { OVERLAY_TYPE, useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { useUserContext } from '@ulta/core/providers/UserContextProvider/UserContextProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import datacapture from '@ulta/core/utils/datacapture/datacapture';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import Tag from '@ulta/modules/Tag/Tag';

import EarlyAccessContent from '@ulta/components/EarlyAccessContent/EarlyAccessContent';
import FulfillmentButton from '@ulta/components/FulfillmentButton/FulfillmentButton';
import NotifyMeV2 from '@ulta/components/NotifyMeV2/NotifyMeV2';
import PdpSelectableCard from '@ulta/components/PdpSelectableCard/PdpSelectableCard';
import ProductComingSoon from '@ulta/components/ProductComingSoon/ProductComingSoon';
import ProductPartnerInfo from '@ulta/components/ProductPartnerInfo/ProductPartnerInfo';
import ProductPreSale from '@ulta/components/ProductPresale/ProductPresale';
import SellerInfo from '@ulta/components/SellerInfo/SellerInfo';
import StickyContainer from '@ulta/components/StickyContainer/StickyContainer';
import Subscription from '@ulta/components/Subscription/Subscription';
import TileDeliveryInfo from '@ulta/components/TileDeliveryInfo/TileDeliveryInfo';

import constants from '@ulta/utils/constants/constants';

import * as utils from './ProductActionsV2';

export const secondaryEligibilityStates = ['change_store', 'change_address'];
export const disabledEligibilityStates = ['out_of_stock', 'notify_me'];

export const ProductActionsV2 = ( props ) => {
  const {
    bookmarkAction,
    bookmarked,
    comingSoon,
    isGWP,
    componentKey,
    displayState,
    fulfillmentSection = {},
    invokeAction,
    invokeMutation,
    messages,
    notifyMe,
    perk,
    productId,
    partnerInfo,
    sellerInfo,
    removeBookmarkAction,
    skuId,
    viewItemsInOfferAction,
    updateSuccessLabel,
    removeSubscriptionMessage,
    inStore,
    isWebStickyATBButton,
    stickyAddToBagAccessibilityLabel,
    earlyAccessInfo,
    earlyAccessAction,
    appShopEarlyAction,
    appPresaleAction,
    appPresaleInfo,
    promoMessage,
    wishListAction,
    hasOneItemInWishList,
    isWishListEnabledInPDP,
    appExclusiveRedirectUrl,
    brandName,
    sellerId,
    loading
  } = props;


  const { cart, store, sddStore, sddInfo } = useUserContext();
  const [isFulfillmentChanged, setIsFulfillmentChanged] = useState( false );
  // Omit isFulfillmentChanged from deps: if it flips, onModuleUpdate would run immediately and
  // STATE.UPDATE would apply payload: props while the parent may still show Ship, reverting SDD.
  // When the effect *does* run (cart/sdd/fulfillment, etc.), this closure is from that render, so
  // isSubscribed/flags here are current; no ref needed.
  useEffect( () => {
    utils.onModuleUpdate(
      { fulfillmentSection, props, cart, store, sddStore, sddInfo, isFulfillmentChanged },
      { setIsSubscribed, setSubscriptionFrequency, setShowUpdateBag, setPrevLabel, setOriginalModuleState, dispatch }
    );
  }, [fulfillmentSection, perk, cart, store, sddStore, sddInfo] );
  const { breakpoint } = useDeviceInflection();
  const isLargeDevice = breakpoint?.isLargeDevice();
  const isSmallDevice = breakpoint?.isSmallDevice();
  const [state, dispatch] = useReducer( utils.fulfillmentStateReducer, props );
  const [showUpdateBag, setShowUpdateBag] = useState( false );
  const [isSubscribed, setIsSubscribed] = useState( false );
  const [originalModuleState, setOriginalModuleState] = useState( {} );

  // User's selected fulfillment ( or default from DXL )
  const selectedFulfillment  = utils.getSelectedFulfillment( state.fulfillmentSection?.fulfillmentTileOptions ) || {};
  const { eligibilityState, deliveryInfo, subscription } = selectedFulfillment;

  const removeWishListAction = utils.getRemoveWishListAction( { hasOneItemInWishList, removeBookmarkAction, wishListAction, isWishListEnabledInPDP } );

  // User's selected subscription and frequency
  const [subscriptionFrequency, setSubscriptionFrequency] = useState( subscription?.frequencyOptions?.value );
  const [prevLabel, setPrevLabel] = useState( selectedFulfillment.buttonAction?.label );

  const isSecondary = utils.isSecondaryState( { eligibilityState } );
  const isDisabled = utils.isDisabledState( { eligibilityState } );
  const isMyListOverlayOpen = useRef( false );

  const { openOverlay, closeOverlay } = useOverlay();

  useEffect( () => {
    if( isMyListOverlayOpen && closeOverlay ){
      closeOverlay();
    }
  }, [wishListAction] );

  useEffect( () => {
    utils.resetFulfillmentStateOnSkuChange( { setIsFulfillmentChanged, setShowUpdateBag } );
  }, [skuId] );

  // Adds a class to the body when ATB button is sticky on mobile to prevent overlapping with footer links
  useEffect( () => {
    if( !isLargeDevice && isWebStickyATBButton ){
      document.body.classList.add( 'has-sticky-atb-pdp' );
    }

    return () => {
      document.body.classList.remove( 'has-sticky-atb-pdp' );
    };
  }, [isLargeDevice, isWebStickyATBButton] );


  const components = getRenderComponents( {
    comingSoon, displayState, inStore, notifyMe, PDP_ACTIONS_DISPLAY_STATE,
    messages, perk, removeSubscriptionMessage, selectedFulfillment, showUpdateBag, deliveryInfo,
    earlyAccessInfo, earlyAccessAction, appShopEarlyAction, appPresaleAction
  },
  { hasItems }
  );

  const subscriptionChangeHandler = useCallback(
    utils.subscriptionChangeHandler(
      { prevLabel, selectedFulfillment, originalModuleState, subscriptionFrequency },
      { setIsSubscribed, setSubscriptionFrequency, setShowUpdateBag, dispatch } ),
    [selectedFulfillment, prevLabel, originalModuleState]
  );

  const frequencyChangeHandler = useCallback(
    utils.frequencyChangeHandler(
      { selectedFulfillment, originalModuleState },
      { setSubscriptionFrequency, setShowUpdateBag, dispatch } ),
    [selectedFulfillment, originalModuleState]
  );

  const fulfillmentChangeHandler = useCallback(
    utils.fulfillmentChangeHandler( { skuId, productId, isSubscribed, subscriptionFrequency, originalModuleState, fulfillmentOptions: state.fulfillmentSection?.fulfillmentTileOptions, cart, store, sddStore, sddInfo }, { dispatch, invokeAction, datacapture, setShowUpdateBag, setIsFulfillmentChanged, setIsSubscribed } ),
    [skuId, productId, isSubscribed, subscriptionFrequency, originalModuleState, cart, store, sddStore, sddInfo]
  );

  const renderInlineMessages = () => {
    if( components.inlineMessages ){
      return components.inlineMessages.map( ( messageItem, index ) => (
        <div key={ `${index}-${componentKey}` }
          className='ProductActionsV2--info-message'
        >
          <InlineMessage
            message={ messageItem.message }
            messageType={ messageItem.icon }
            { ...( messageItem.icon === 'error' && { role: 'alert' } ) }
          />
        </div>
      ) );
    }
    return null;
  };

  const stickyContent =
  (
    <>
      { ( components.addToBag ) &&
        <AddToBag
          { ...spreadProps( { ...state } ) }
          isWebStickyATBButton={ isWebStickyATBButton }
          stickyAddToBagAccessibilityLabel={ stickyAddToBagAccessibilityLabel }
          selectedFulfillment={ selectedFulfillment }
          isSecondary={ isSecondary }
          isDisabled={ isDisabled }
          isSubscribed={ isSubscribed }
          subscriptionFrequency={ subscriptionFrequency }
          invokeAction={ invokeAction }
          updateSuccessLabel={ updateSuccessLabel }
          showUpdateBag={ showUpdateBag }
          setIsFulfillmentChanged={ setIsFulfillmentChanged }
          wishListAction={ wishListAction }
          isMyListOverlayOpen={ isMyListOverlayOpen }
          removeWishListAction={ removeWishListAction }
          loading={ loading }
        />
      }
      {
        components.isEarlyAccess &&
        <EarlyAccess
          { ...spreadProps( { ...props } ) }
          isSmallDevice={ isSmallDevice }
          openOverlay={ openOverlay }
          promoMessage={ promoMessage }
          isMyListOverlayOpen={ isMyListOverlayOpen }
          fulfillmentType={ selectedFulfillment?.fulfillmentType }
          removeBookmarkAction={ removeWishListAction }
          appExclusiveRedirectUrl={ appExclusiveRedirectUrl }
        />
      }
      { renderInlineMessages() }
      { components.subscriptionMessages &&
        <InlineMessage
          data-testid='inline-message'
          role='status'
          message={ removeSubscriptionMessage.message }
          messageType={ removeSubscriptionMessage.icon }
        />
      }
      { viewItemsInOfferAction &&
        <ViewItemsInOffer
          { ...spreadProps( { ...state } ) }
          viewItemsInOfferAction={ viewItemsInOfferAction }
        />
      }
    </>
  );
  const addToBagContent = renderWithStickyContainer( { isLargeDevice, isWebStickyATBButton, component: stickyContent } );

  const componentRef = useRef();
  useIntersectionProcessor( { ...props, componentRef } );

  return (
    <div className='ProductActionsV2'
      ref={ componentRef }
    >
      <div className='FulfillmentSection'>
        <fieldset>
          { state.fulfillmentSection &&
          <>
            <legend className='FulfillmentSection__heading'>
              <Text
                htmlTag={ state.fulfillmentSection.title?.htmlTag }
                textStyle='title'
              >
                { state.fulfillmentSection.title.text }
              </Text>
            </legend>
            { components.fulfillmentTiles &&
            <ul className='FulfillmentSection__tiles'>
              <utils.FulfillmentTiles
                { ...spreadProps( { ...state } ) }
                onChange={ fulfillmentChangeHandler }
              />
            </ul>
            }
          </>
          }
          { deliveryInfo &&
          <TileDeliveryInfo
            { ...spreadProps( { ...deliveryInfo } ) }
            invokeAction={ invokeAction }
            productId={ productId }
            skuId={ skuId }
          />
          }
          { components.showAlternateStoreMessage &&
          <div className='ProductActionsV2__alternateStoreTag'>
            <Tag
              variant='utility'
              style='validation'
              message={ deliveryInfo?.alternateStoreTag?.message }
            />
          </div>
          }
          { !!selectedFulfillment.subscription &&
          <div className='ProductActionsV2__Susbcription'>
            <Subscription
              { ...spreadProps( { ...selectedFulfillment.subscription } ) }
              onSubscriptionChange={ subscriptionChangeHandler }
              onSubscriptionFrequencyChange={ frequencyChangeHandler }
              invokeAction={ invokeAction }
            />
          </div>
          }
          { partnerInfo &&
          <ProductPartnerInfo { ...partnerInfo } />
          }

          { sellerInfo &&
          <SellerInfo
            { ...spreadProps( { ...sellerInfo } ) }
            invokeAction={ invokeAction }
            brandName={ brandName }
            sellerId={ sellerId }
            showDivider={ true }
            className={ 'SellerInfo_PDP' }
          />
          }
          <div className='ProductActionsV2--bottom-spacer'>
            { components.notifyMe &&
            <NotifyMeV2
              { ...spreadProps( { ...props, ...notifyMe } ) }
              invokeMutation={ invokeMutation }
              fulfillmentType={ selectedFulfillment?.fulfillmentType }
              removeBookmarkAction={ removeWishListAction }
            /> }
            { addToBagContent }
            { components.perk &&
            <utils.PlatinumPerk
              { ...spreadProps( { ...state } ) }
              isMyListOverlayOpen={ isMyListOverlayOpen }
              wishListAction={ wishListAction }
              fulfillmentType={ selectedFulfillment?.fulfillmentType }
              removeBookmarkAction={ removeWishListAction }
            /> }
            { components.comingSoon &&
            <utils.ComingSoon
              { ...spreadProps( { ...props } ) }
              comingSoonLabel={ comingSoon.comingSoonLabel }
              comingSoonMessage={ comingSoon.comingSoonMessage }
              bookmarked={ bookmarked }
              bookmarkAction={ bookmarkAction }
              removeBookmarkAction={ removeWishListAction }
              invokeMutation={ invokeMutation }
              productId={ productId }
              skuId={ skuId }
              wishListAction={ wishListAction }
              isMyListOverlayOpen={ isMyListOverlayOpen }
              fulfillmentType={ selectedFulfillment?.fulfillmentType }
            /> }

            { components.inStore &&
            <utils.InStoreOnlyItem
              { ...spreadProps( { ...props } ) }
              message={ inStore.message }
              bookmarkAction={ bookmarkAction }
              removeBookmarkAction={ removeWishListAction }
              invokeMutation={ invokeMutation }
              productId={ productId }
              skuId={ skuId }
              isMyListOverlayOpen={ isMyListOverlayOpen }
              fulfillmentType={ selectedFulfillment?.fulfillmentType }
            />
            }
          </div>
        </fieldset>
      </div>
    </div>
  );
};

/**
 * Function to return FulfillmentTiles.
 *
 * @method FulfillmentTiles
 * @param {object} fulfillmentSection - fulfillmentSection.
 * @param {function} onChange - onChange function
 * @returns {JSX.Element|null} A list of `PdpSelectableCard`
 **/
export const FulfillmentTiles = ( props ) => {
  const {
    fulfillmentSection,
    onChange
  } = props || {};

  // Early return
  if( !fulfillmentSection ){
    return null;
  }

  return (
    fulfillmentSection.fulfillmentTileOptions?.map( ( tile ) => {
      return (
        <PdpSelectableCard
          alignment='center'
          fulfillmentType={ tile.fulfillmentType }
          selectAction={ tile.selectAction }
          updateVariantAction={ tile.updateVariantAction }
          icon={ tile.icon }
          key={ tile.fulfillmentType }
          onChange={ onChange }
          selected={ tile.selected }
          subtitle={ tile.subtitle }
          tileState={ tile.tileState }
          title={ tile.title }
        >
        </PdpSelectableCard>
      );
    } )
  );
};

/**
 * Return a `AddtoBag` button with favourties button next to it.
 *
 * @param {Object} props - The props passed to the component.
 * @param {Object} props.bookmarkAction - The action to add a bookmark .
 * @param {Object} props.removeBookmarkAction - The action to remove a bookmark.
 * @param {boolean} props.bookmarked - Indicates if the item is bookmarked.
 * @param {string} props.commerceId - The commerce ID.
 * @param {string} props.productId - The product ID.
 * @param {number} props.quantity - The quantity of the item to add.
 * @param {string} props.skuId - The SKU ID.
 * @param {Object} props.selectedFulfillment - the selected fulfillment option.
 * @param {boolean} props.isSecondary - if the button should appear as a secondary button.
 * @param {boolean} props.isDisabled - if the button should be disabled.
 * @returns {JSX.Element} returns the `FulfillmentButton` component.
 */
export const AddToBag = ( props ) => {
  const {
    bookmarkAction,
    invokeMutation,
    removeBookmarkAction,
    bookmarked,
    commerceId,
    productId,
    quantity,
    skuId,
    selectedFulfillment,
    isSecondary,
    inBagLabel,
    incrementBagAccessibilityLabel,
    successLabel,
    isDisabled,
    invokeAction,
    isSubscribed,
    subscriptionFrequency,
    updateSuccessLabel,
    showUpdateBag,
    setIsFulfillmentChanged,
    isWebStickyATBButton,
    stickyAddToBagAccessibilityLabel,
    wishListAction,
    hasOneItemInWishList,
    brandName,
    isMyListOverlayOpen,
    removeWishListAction,
    loading
  } = props;
  return (
    <FulfillmentButton
      { ...spreadProps( { ...selectedFulfillment } ) }
      isWebStickyATBButton={ isWebStickyATBButton }
      stickyAddToBagAccessibilityLabel={ stickyAddToBagAccessibilityLabel }
      invokeMutation={ invokeMutation }
      bookmarkAction={ bookmarkAction }
      bookmarked={ bookmarked }
      commerceId={ commerceId }
      productId={ productId }
      quantity={ quantity }
      inBagLabel={ inBagLabel }
      skuId={ skuId }
      brandName={ brandName }
      isSecondaryState={ isSecondary }
      isDisabled={ isDisabled }
      incrementBagAccessibilityLabel={ incrementBagAccessibilityLabel }
      removeBookmarkAction={ removeBookmarkAction }
      successLabel={ successLabel }
      isSubscribed={ isSubscribed }
      subscriptionFrequency={ subscriptionFrequency }
      invokeAction={ invokeAction }
      updateSuccessLabel={ updateSuccessLabel }
      showUpdateBag={ showUpdateBag }
      setIsFulfillmentChanged={ setIsFulfillmentChanged }
      wishListAction={ wishListAction }
      hasOneItemInWishList={ hasOneItemInWishList }
      isDisabledForNotify={ selectedFulfillment?.eligibilityState === PDP_ACTIONS_DISPLAY_STATE.NOTIFY_ME }
      isMyListOverlayOpen={ isMyListOverlayOpen }
      removeWishListAction={ removeWishListAction }
      loading={ loading }
    />
  );
};

/**
 * Returns a button inside a div for ViewItemsInOffer.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.viewItemsInOfferAction - The action for the button.
 * @param {Object} props.fulfillmentSection - details about the fulfillment section.
 * @returns {JSX.Element} returns a 'ViewItemsInOffer' component.
 */
export const ViewItemsInOffer = ( props ) => {
  const {
    viewItemsInOfferAction,
    isSaleableGwp
  } = props;
  const viewItemsButton = (
    <div className='viewItemsinOffer' >
      <Link_Huge className='viewItemsinOfferButton'
        likeButtonOutline={ isSaleableGwp }
        likeButtonSecondary={ !isSaleableGwp }
        action={ viewItemsInOfferAction }
      >
        { viewItemsInOfferAction?.label }
      </Link_Huge>
    </div>
  );
  return viewItemsButton;
};

/**
 * Returns a `ProductPreSale` component with perk and bookmark details.
 *
 * @param {Object} props - The properties to the component.
 * @param {Object} props.perk - The perk details to display.
 * @param {boolean} props.bookmarked - if the perk is bookmarked.
 * @param {Object} props.bookmarkAction - action to add a bookmark.
 * @param {Object} props.removeBookmarkAction - action to remove a bookmark.
 * @returns {JSX.Element} return the `ProductPreSale` .
 */
export const PlatinumPerk = ( props ) => {
  const {
    perk,
    bookmarked,
    bookmarkAction,
    removeBookmarkAction,
    wishListAction,
    isMyListOverlayOpen,
    fulfillmentType
  } = props;
  return (
    <ProductPreSale
      { ...spreadProps( { ...perk } ) }
      bookmarked={ bookmarked }
      bookmarkAction={ bookmarkAction }
      removeBookmarkAction={ removeBookmarkAction }
      wishListAction={ wishListAction }
      isMyListOverlayOpen={ isMyListOverlayOpen }
      fulfillmentType={ fulfillmentType }
      { ...spreadProps( { ...props } ) }
    />
  );
};

/**
 * A wrapper component for rendering the `ProductComingSoon` component.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.comingSoonLabel - The label text to display for the "coming soon" section.
 * @param {string} props.comingSoonMessage - The message to display in the "coming soon" section.
 * @param {boolean} props.bookmarked - Indicates whether the item is currently bookmarked.
 * @param {Object} props.bookmarkAction - action to handle bookmarking the item.
 * @param {Object} props.removeBookmarkAction - action to handle removing the bookmark.
 * @returns {JSX.Element} The `ProductComingSoon` component
 */
export const ComingSoon = ( props ) => {
  const {
    comingSoonLabel,
    comingSoonMessage,
    bookmarked,
    bookmarkAction,
    removeBookmarkAction
  } = props;

  return (
    <ProductComingSoon
      comingSoonLabel={ comingSoonLabel }
      comingSoonMessage={ comingSoonMessage }
      bookmarked={ bookmarked }
      bookmarkAction={ bookmarkAction }
      removeBookmarkAction={ removeBookmarkAction }
      { ...spreadProps( { ...props } ) }
    />
  );
};

/**
 * A wrapper component for rendering the `InStoreOnlyItem` component.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.message - The label text to display for the "Available for In - Store Purchase only" section.
 * @param {boolean} props.bookmarked - Indicates whether the item is currently bookmarked.
 * @param {Object} props.bookmarkAction - action to handle bookmarking the item.
 * @param {Object} props.removeBookmarkAction - action to handle removing the bookmark.
 */
export const InStoreOnlyItem = ( props ) => {
  const { message, isMyListOverlayOpen, wishListAction, fulfillmentType } = props;
  if( !message ){
    return;
  }
  return (
    <div className='InStoreOnlyItem'>
      <Tag
        variant='utility'
        style='notification'
        message={ message }
      />
      <AsyncComponent
        id={ `ProductBookmark-${props.productId}-${props.skuId}` }
        bookmarkAction={ wishListAction || props.bookmarkAction }
        bookmarked={ props.bookmarked }
        commerceId={ props.commerceId }
        invokeMutation={ props.invokeMutation }
        key='ProductBookmark'
        moduleName='ProductBookmark'
        productActionsProps={ props }
        productId={ props.productId }
        quantity={ props.quantity }
        removeBookmarkAction={ props.removeBookmarkAction }
        skuId={ props.skuId }
        state={ props.state }
        isMyListOverlayOpen={ isMyListOverlayOpen }
        fulfillmentType={ fulfillmentType }
        loading={ props.loading }
      />
    </div>

  );
};

/**
 * Renders the Early Access section with an unlock button and my list component.
 *
 * @component
 * @param {Object} props.earlyAccessInfo - info related to early access .
 * @param {Function} props.earlyAccessAction - Callback for early access actions .
 * @param {string} props.id - Unique id .
 * @param {Function} props.bookmarkAction - to handle the bookmark action.
 * @param {boolean} props.bookmarked - Indicates whether the item is bookmarked.
 * @param {string} props.commerceId - Commerce ID of the product.
 * @param {Function} props.invokeMutation - Function to invoke a mutation.
 * @param {string} props.productId - Product Id.
 * @param {number} props.quantity - Quantity of the product.
 * @param {Function} props.removeBookmarkAction - Function to handle removing a bookmark.
 * @param {string} props.skuId - SKU Id.
 * @param {Object} props.state - State object.
 *
 * @returns {JSX.Element} The rendered EarlyAccess section.
 */
export const EarlyAccess = ( props ) => {
  const { earlyAccessInfo, earlyAccessAction, appShopEarlyAction, appPresaleAction, isSmallDevice, appPresaleInfo, openOverlay, renderEarlyAccessContentCardOverlay, promoMessage, wishListAction, isMyListOverlayOpen, fulfillmentType, appExclusiveRedirectUrl } = props;

  const isAppOnlySku = !! ( appShopEarlyAction || appPresaleAction );

  const earlyAccessLabel =  ( isAppOnlySku && ( isSmallDevice ? appShopEarlyAction?.label : appPresaleAction?.label ) ) || earlyAccessAction?.label;

  return (
    <div className='EarlyAccess'>
      <div className='EarlyAccess__Unlock'>
        <Button
          width='fullWidth'
          className='EarlyAccess__Button'
          label={ earlyAccessLabel } // TODO : DXL intergation
          disabled={ false }
          action={ null }
          onClick={ () => utils.earlyAccessClickHandler( { isAppOnlySku, isSmallDevice, appPresaleInfo, earlyAccessInfo, appShopEarlyAction, appPresaleAction, appExclusiveRedirectUrl }, { openOverlay, renderEarlyAccessContentCardOverlay } ) }
          { ...( !isAppOnlySku && {
            iconName: 'lock',
            iconPosition: 'before'
          } ) }
        />
        <div className='EarlyAccess__Favorite'>
          <AsyncComponent
            bookmarkAction={ wishListAction || props.bookmarkAction }
            bookmarked={ props.bookmarked }
            commerceId={ props.commerceId }
            invokeMutation={ props.invokeMutation }
            key='ProductBookmark'
            moduleName='ProductBookmark'
            productActionsProps={ props }
            productId={ props.productId }
            quantity={ props.quantity }
            removeBookmarkAction={ props.removeBookmarkAction }
            skuId={ props.skuId }
            state={ props.state }
            isMyListOverlayOpen={ isMyListOverlayOpen }
            fulfillmentType={ fulfillmentType }
            loading={ props.loading }
          />
        </div>
      </div>
      <InlineMessage
        message={ promoMessage?.message } // TODO : DXL intergation
        messageType={ 'infocircle' } // TODO : DXL intergation
      ></InlineMessage>
    </div>
  );
};

export const earlyAccessClickHandler = ( data, methods ) => {
  const { isAppOnlySku, isSmallDevice, appPresaleInfo, earlyAccessInfo, appExclusiveRedirectUrl } = data;
  const { openOverlay } = methods;

  if( isAppOnlySku && isSmallDevice ){
  // TODO: Navigate to native app
    utils.openNativeApp( { appExclusiveRedirectUrl } );
    return;
  }

  const modalData = isAppOnlySku ? appPresaleInfo : earlyAccessInfo;
  // TODO: Open the modal using modalData
  openOverlay( {
    onClose: () => { },
    type: ( isSmallDevice ) ? OVERLAY_TYPE.BottomSheet : OVERLAY_TYPE.Modal,
    alignment: 'center',
    crossButtonVisibility: false,
    disableClose: false,
    content:  <EarlyAccessContent { ...modalData } />
  } );

};

export const openNativeApp = ( data ) => {
  const { appExclusiveRedirectUrl } = handleEmptyObjects( data );

  const isIOS = /iphone|ipad|ipod/i.test( navigator.userAgent );

  if( !appExclusiveRedirectUrl ){
    const storeUrl = isIOS ? constants.IOS_APP_URL : constants.ANDROID_APP_URL;
    window.location.href = storeUrl;
    return;
  }

  const { deepLinkURL, appStoreURL, playStoreURL, redirectURL } = handleEmptyObjects( appExclusiveRedirectUrl );

  // need open the native android and ios app from here if app is not installed open the playstore or appstore
  const deeplink = encodeURIComponent( deepLinkURL );
  const fallback = encodeURIComponent( isIOS ? appStoreURL : playStoreURL );

  // Step 1: split redirect URL into base and query string
  const [baseUrl, queryString] = redirectURL.split( '?' );

  // Step 2: split query string into params (in this case deeplink and fallback)
  const params = queryString.split( '&' );

  // Step 3: replace %1 and %2 in each param individually
  const updatedParams = params.map( ( param ) => {
    if( param.includes( '%1' ) ){
      return param.replace( '%1', deeplink );
    }
    if( param.includes( '%2' ) ){
      return param.replace( '%2', fallback );
    }
    return param;
  } );

  const finalRedirectUrl = `${baseUrl}?${updatedParams.join( '&' )}`;

  window.location.href = finalRedirectUrl;
};

export const resetFulfillmentStateOnSkuChange = ( { setIsFulfillmentChanged, setShowUpdateBag } ) => {
  setIsFulfillmentChanged( false );
  setShowUpdateBag( false );
};


/**
 * Gets the selected fulfillment option from a list based on the given fulfillment type.
 *
 * @param {Array<Object>} arr - The list of fulfillment options.
 * @param {string} type - The fulfillment type
 * @returns {Object} The selected fulfillment option.
 */
export const getSelectedFulfillment = ( arr, type ) => {
  // early return
  if( !hasItems( arr ) ){
    return;
  }
  let foundFulfillment;
  if( type ){
    // if type is passed return fulfillmentType based on type or return default if not found
    foundFulfillment = arr.filter( ( item ) => item.fulfillmentType === type )[0];
  }
  else {
    // otherwise return the selected fulfillmentType
    foundFulfillment = arr.filter( ( item ) => item.selected )[0];
  }
  // return the found fulfillment or default
  return foundFulfillment || getSelectedFulfillment( arr, fulfillmentTypes.SHIP );
};

/**
 * Updates the state to the selected fulfillment type in the fulfillment options.
 *
 * @param {Object} state - The current state.
 * @param {Object} action - action to reducer function to update new state.
 * @returns {Object} The updated state with the selected fulfillment type.
 */
export const fulfillmentStateReducer = ( state, action ) => {
  const { type = fulfillmentTypes.SHIP } = action || {};
  switch ( type ){
    case STATE.UPDATE:
      const { cart = {}, store = {}, sddStore = {}, sddInfo = {}, isFulfillmentChanged } = action || {};
      const { cartBopisStoreId, cartSddStoreId, cartSddInfo } = cart;
      const { setIsSubscribed, setSubscriptionFrequency, setPrevLabel, setOriginalModuleState, setShowUpdateBag } = action.methods;
      const lastUpdateSnapshot = utils.getSelectedFulfillment( action.fulfillmentSection?.fulfillmentTileOptions );
      if( lastUpdateSnapshot ){
        const isNotifyMe = lastUpdateSnapshot.eligibilityState === PDP_ACTIONS_DISPLAY_STATE.NOTIFY_ME;
        if( isNotifyMe ){
          setShowUpdateBag( false );
        }
        else {
          const isFulfillmentTypeShip = lastUpdateSnapshot.fulfillmentType === fulfillmentTypes.SHIP;
          const isBopisStoreUnChanged = !isFulfillmentChanged && lastUpdateSnapshot.fulfillmentType === fulfillmentTypes.BOPIS && cartBopisStoreId === store?.bopisStoreId;
          const isSddStoreInfoUnChanged = !isFulfillmentChanged && lastUpdateSnapshot.fulfillmentType === fulfillmentTypes.SDD && cartSddStoreId === sddStore?.sddStoreId && isSddInfoMatches( cartSddInfo?.address, sddInfo?.sddInfo?.address );
          if( isFulfillmentTypeShip || isBopisStoreUnChanged || isSddStoreInfoUnChanged ){
            setPrevLabel( lastUpdateSnapshot.buttonAction?.label );
            if( isFulfillmentTypeShip ){
              setIsSubscribed( lastUpdateSnapshot.subscription?.subsAndSaveOptIn?.value );
              setSubscriptionFrequency( lastUpdateSnapshot.subscription?.frequencyOptions?.value );
              updateOriginalModuleState( { type: lastUpdateSnapshot?.fulfillmentType,
                isSubscribed: lastUpdateSnapshot?.subscription?.subsAndSaveOptIn?.value,
                subscriptionFrequency: lastUpdateSnapshot?.subscription?.frequencyOptions?.value
              }, { setOriginalModuleState } );
            }
            else {
              // R&S (Replenish & Save) is ship-only; do not re-apply opt-in for BOPIS/SDD when module syncs (DCOM-13358)
              setIsSubscribed( false );
              setSubscriptionFrequency( '' );
              updateOriginalModuleState( { type: lastUpdateSnapshot?.fulfillmentType, isSubscribed: false, subscriptionFrequency: '' },
                { setOriginalModuleState } );
            }
          }
          else {
            // Non-ship + store/address out of sync (e.g. returning from SDD address flow) or fulfillment in flux:
            // still clear R&S in React state — the branch above only ran when BOPIS/SDD was fully "stable", so
            // isSubscribed could stay true from SHP and break add-to-bag (DCOM-13358).
            if( lastUpdateSnapshot?.fulfillmentType !== fulfillmentTypes.SHIP ){
              setIsSubscribed( false );
              setSubscriptionFrequency( '' );
            }
            if( !isFulfillmentChanged && lastUpdateSnapshot?.fulfillmentType !== fulfillmentTypes.SHIP ){
              updateOriginalModuleState( { type: lastUpdateSnapshot?.fulfillmentType, isSubscribed: false, subscriptionFrequency: '' },
                { setOriginalModuleState } );
            }
            setShowUpdateBag( true );
          }
        }
      }
      return action.payload; // Update the state with the new props
    case STATE.SUBSCRIPTION_TOGGLE:
      return {
        ...state,
        fulfillmentSection: {
          ...state.fulfillmentSection,
          fulfillmentTileOptions: state.fulfillmentSection?.fulfillmentTileOptions?.map( ( item ) => {
            if( item.subscription?.subsAndSaveOptIn?.disabled === false ){
              return {
                ...item,
                buttonAction: {
                  ...item.buttonAction,
                  label: action.payload.isSubscriptionOpted ? item.subscription?.addToBagSubscriptionLabel : action.payload.prevLabel
                }
              };
            }
            return item;
          } )
        }
      };
    case STATE.SUBSCRIPTION_FREQ_CHANGE:
      return {
        ...state,
        fulfillmentSection: {
          ...state.fulfillmentSection,
          fulfillmentTileOptions: state.fulfillmentSection?.fulfillmentTileOptions?.map( ( item ) => {
            if( item.subscription?.subsAndSaveOptIn?.disabled === false ){
              return {
                ...item,
                subscription: {
                  ...item.subscription,
                  frequencyOptions: {
                    ...item.subscription?.frequencyOptions,
                    value: action.payload.subscriptionFrequency
                  }
                }
              };
            }
            return item;
          } )
        }
      };
    default:
      return {
        ...state,
        fulfillmentSection:
        {
          ...state.fulfillmentSection,
          fulfillmentTileOptions: state.fulfillmentSection?.fulfillmentTileOptions?.map( ( item ) => {
            return {
              ...item,
              selected: item.fulfillmentType === type
            };
          } )
        }
      };
  }
};

/**
   * Function to determine which components should render.
   *
   * @param {object} data - object containing data for the components
   * @param {object} method - object containing helper functions (e.g. hasItems)
   * @returns {object} object with computed boolean values for each component
   */
export const getRenderComponents = ( data, method ) => {
  const {
    comingSoon,
    displayState,
    inStore,
    messages = [],
    notifyMe,
    PDP_ACTIONS_DISPLAY_STATE,
    perk,
    selectedFulfillment,
    showUpdateBag,
    deliveryInfo,
    removeSubscriptionMessage,
    earlyAccessAction,
    earlyAccessInfo,
    appShopEarlyAction,
    appPresaleAction
  } = data;
  const { hasItems } = method;

  // Helper function to determine if "comingSoon" should be true
  const isComingSoon = () => {
    return !!( comingSoon && displayState === PDP_ACTIONS_DISPLAY_STATE.COMING_SOON );
  };

  // Helper function to determine if "fulfillmentTiles" should be true
  const isFulfillmentTiles = () => {
    return !!( displayState === PDP_ACTIONS_DISPLAY_STATE.FULFILLMENT_TILE );
  };

  // Helper function to determine if "inStore" should be true
  const isInStore = () => {
    return !!( inStore && hasItems( Object.keys( inStore ) ) );
  };

  // Helper function to determine if "notifyMe" should be true
  const isNotifyMe = () => {
    return !!( notifyMe?.notifyMeAction && selectedFulfillment.eligibilityState === PDP_ACTIONS_DISPLAY_STATE.NOTIFY_ME );
  };

  // Helper function to determine if "perk" should be true
  const isPerk = () => {
    return !!( perk && perk?.perkAction && displayState === PDP_ACTIONS_DISPLAY_STATE.PERK );
  };

  const showAddToBag = () => {
    if( notifyMe?.notifyMeAction && selectedFulfillment.eligibilityState === PDP_ACTIONS_DISPLAY_STATE.NOTIFY_ME ){
      return true;
    }
    return !!( selectedFulfillment.buttonAction || selectedFulfillment.addToBagAction || selectedFulfillment.updateBagAction );
  };

  // Helper function to combine messages
  const showInlineMessages = () => {
    if( !( messages?.length > 0 ) && !( selectedFulfillment.messages?.length > 0 ) ){
      return false;
    }
    else if( selectedFulfillment.messages?.length > 0 ){
      return [...( selectedFulfillment?.messages || [] ), ...( messages || [] )];
    }
    else {
      return messages;
    }
  };
  // Helper function to determine if "subscriptionMessages" should be true
  const showSubscriptionMessages = ()=> {
    return !!( showUpdateBag && removeSubscriptionMessage && selectedFulfillment.updateBagAction && selectedFulfillment.subscription?.subsAndSaveOptIn?.disabled );
  };

  const showAlternateStore = () => {
    return !! ( deliveryInfo?.alternateStoreTag?.message );
  };

  const isEarlyAccess = () => {
    return !! ( earlyAccessAction || earlyAccessInfo || appShopEarlyAction || appPresaleAction );
  };


  // Return the components object with computed values
  return {
    addToBag: showAddToBag(),
    comingSoon: isComingSoon(),
    fulfillmentTiles: isFulfillmentTiles(),
    inlineMessages: showInlineMessages(),
    inStore: isInStore(),
    notifyMe: isNotifyMe(),
    perk: isPerk(),
    subscriptionMessages: showSubscriptionMessages(),
    showAlternateStoreMessage: showAlternateStore(),
    isEarlyAccess : isEarlyAccess()
  };
};

/**
 * Updates the original module state with the passed data
 *
 * @param {object} data - Data to be saved in original module state
 * @param {string} methods - Method to be called to save to the data to original module state
 */
export const updateOriginalModuleState = ( data, methods ) => {
  const { setOriginalModuleState } = methods;
  setOriginalModuleState( data );
};

/**
 * z-index for sticky Add to Bag bar on mWeb PDP (below overlays at 998+).
 */
export const STICKY_ATB_Z_INDEX = 3;


/**
 * Wraps a component in a StickyContainer if isSticky is true, otherwise returns the component as is.
 *
 * @param {boolean} isLargeDevice - Whether the current device is large (desktop).
 * @param {boolean} isWebStickyATBButton - Whether sticky Add to Bag is enabled.
 * @returns {ReactNode} The wrapped component or the original component.
 */
export const renderWithStickyContainer = ( props ) => {
  const { isLargeDevice, isWebStickyATBButton, component } = props;

  if( !isLargeDevice && isWebStickyATBButton ){
    const stickyBar = (
      <StickyContainer
        position='bottom'
        fullWidth={ true }
        zIndex={ STICKY_ATB_Z_INDEX }
        customClassName='ProductActionsV2__AddToBagSticky'
      >
        { component }
      </StickyContainer>
    );

    if( typeof document !== 'undefined' ){
      return createPortal( stickyBar, document.body );
    }

    return stickyBar;
  }

  return component;
};

/**
 * Checks if the given eligibility state is in the list of secondary states.
 *
 * @param {Array<string>} secondaryEligibilityStates - A list of secondary states.
 * @param {string} eligibilityState - The state to check.
 * @returns {boolean} `true` if the state is a secondary state, otherwise `false`.
 */
export const isSecondaryState = ( { secondaryEligibilityStates = utils.secondaryEligibilityStates, eligibilityState } ) => {
  return secondaryEligibilityStates.some( eligibility => eligibility === eligibilityState );
};

/**
 * Checks if the Cart Sdd Info Matches the updated Sdd Info
 *
 * @param {object} cartAddress - A list of states that are disabled.
 * @param {object} updatedAddress - The state to check.
 * @returns {boolean} `true` if the Cart Sdd Info Matches the updated Sdd Info, otherwise `false`.
 */
export const isSddInfoMatches = ( cartAddress = {}, updatedAddress = {} ) => {
  return ( cartAddress?.address1 === updatedAddress?.address1 &&
    cartAddress?.address2 === updatedAddress?.address2 &&
    cartAddress?.city === updatedAddress?.city &&
    cartAddress?.zipCode === updatedAddress?.zipCode &&
    cartAddress?.state === updatedAddress?.state
  );
};

/**
 * Checks if the given eligibility state is in the list of disabled states.
 *
 * @param {Array<string>} disabledEligibilityStates - A list of states that are disabled.
 * @param {string} eligibilityState - The state to check.
 * @returns {boolean} `true` if the state is disabled, otherwise `false`.
 */
export const isDisabledState = ( { disabledEligibilityStates = utils.disabledEligibilityStates, eligibilityState } ) => {
  return disabledEligibilityStates.some( eligibility => eligibility === eligibilityState );
};


/**
 * Defines the different types of fulfillment options.
 *
 * @type {Object}
 * @property {string} SHIP - "SHIP" fulfillment type.
 * @property {string} BOPIS - "BOPIS" fulfillment type.
 * @property {string} SDD - "SDD" fulfillment type.
 */
export const fulfillmentTypes = {
  SHIP: 'SHP',
  BOPIS: 'PICK',
  SDD: 'SDD'
};

/**
 * Defines the different types of states for reducer.
 *
 * @type {Object}
 * @property {string} UPDATE - "update the props".
 */
export const STATE = {
  UPDATE: 'UPDATE',
  SUBSCRIPTION_TOGGLE: 'SUBSCRIPTION_TOGGLE',
  SUBSCRIPTION_FREQ_CHANGE: 'SUBSCRIPTION_FREQ_CHANGE'
};

/**
 * Defines the different types of states for displayState.
 *
 * @type {Object}
 * @property {string} COMING_SOON - "Coming Soon" display type
 * @property {string} FULFILLMENT_TILE - "Fulfilment tile" display type (ship, pickup, same-day, etc)
 * @property {string} PERK - "Perk" (for rewards member) display type
 */
export const PDP_ACTIONS_DISPLAY_STATE = {
  COMING_SOON: 'COMING_SOON',
  FULFILLMENT_TILE: 'FULFILLMENT_TILE',
  NOTIFY_ME: 'notify_me',
  PERK: 'PERK'
};

/**
 * subscriptionChangeHandler function for subscription onclick event
 * @method
 * @param {object} data Arguments
 * @param {object} methods Methods
 * @param {object} eventData Event Data
 */
export const subscriptionChangeHandler = ( data, method ) => ( eventData ) => {
  const { prevLabel, selectedFulfillment, originalModuleState, subscriptionFrequency } = data;
  const { setIsSubscribed, setSubscriptionFrequency, setShowUpdateBag, dispatch } = method;
  setIsSubscribed( eventData );
  // when user moves from pick to ship the subscriptionFrequency is not set to its default value
  if( eventData === true && !subscriptionFrequency ){
    setSubscriptionFrequency( selectedFulfillment.subscription?.frequencyOptions?.value );
  }
  utils.setUpdateBagButtonStatus( { fulfillmentType: selectedFulfillment?.fulfillmentType, isSubsOpted: eventData, subsFrequency: selectedFulfillment?.subscription?.frequencyOptions?.value, originalModuleState, value: true },
    { setShowUpdateBag }
  );
  dispatch( {
    type: utils.STATE.SUBSCRIPTION_TOGGLE,
    payload: {
      isSubscriptionOpted: eventData,
      prevLabel: prevLabel
    }
  } );
};

/**
 * frequencyChangeHandler function for subscription frequency dropdown change event
 * @method
 * @param {object} methods Methods
 * @param {object} eventData Event Data
 */
export const frequencyChangeHandler = ( data, method ) => ( eventData ) => {
  const { selectedFulfillment, originalModuleState } = data;
  const { setSubscriptionFrequency, setShowUpdateBag, dispatch } = method;
  setSubscriptionFrequency( eventData );
  utils.setUpdateBagButtonStatus( { fulfillmentType: selectedFulfillment?.fulfillmentType, isSubsOpted: selectedFulfillment?.subscription?.subsAndSaveOptIn?.value, subsFrequency: eventData, originalModuleState, value: true },
    { setShowUpdateBag }
  );
  dispatch( {
    type: utils.STATE.SUBSCRIPTION_FREQ_CHANGE,
    payload: {
      subscriptionFrequency: eventData
    }
  } );
};

/**
 * fulfillmentChangeHandler function for fulfillment type change event
 * @method
 * @param {object} methods Methods
 * @param {object} state current state
 * @param {object} action Change requested
 */
export const fulfillmentChangeHandler = ( data, method ) => ( state, action ) => {
  const { selectAction = {}, updateVariantAction } = state;
  const { dispatch, invokeAction, setShowUpdateBag, setIsFulfillmentChanged, setIsSubscribed } = method;
  const { skuId, productId, isSubscribed, subscriptionFrequency, originalModuleState, fulfillmentOptions, cart, store, sddStore, sddInfo } = data;
  const { dataCaptureData } = selectAction;
  if( !dispatch ){
    return;
  }
  dispatch( state, action );
  const selectedFulfillment = utils.getSelectedFulfillment( fulfillmentOptions, state.type );
  const isSubscriptionSelected = selectedFulfillment?.subscription?.subsAndSaveOptIn?.disabled ? false : isSubscribed;
  utils.setUpdateBagButtonStatus( { fulfillmentType: state.type, isSubsOpted: isSubscriptionSelected, subsFrequency: subscriptionFrequency, originalModuleState, cart, store, sddStore, sddInfo, value: true },
    { setShowUpdateBag, setIsFulfillmentChanged }
  );
  selectedFulfillment?.fulfillmentType !== fulfillmentTypes.SHIP && setIsSubscribed( false );
  if( updateVariantAction ){
    invokeAction( {
      ...updateVariantAction,
      variables: {
        moduleParams: { productId, skuId }
      }
    } );
  }

  if( dataCaptureData ){
    datacapture?.processEvents(
      { dataCapture: { ...dataCaptureData } },
      constants.DATACAPTURE_EVENT_TYPE.click
    );
  }
};

/**
 * onModuleUpdate function for reloading all component on update event
 * @method
 * @param {object} data Arguments
 * @param {object} methods Methods
 * @param {object} eventData Event Data
 */
export const onModuleUpdate = ( data, method ) => {
  const { fulfillmentSection, props, cart, store, sddStore, sddInfo, isFulfillmentChanged } = data;
  const { setIsSubscribed, setSubscriptionFrequency, setShowUpdateBag, setPrevLabel, setOriginalModuleState, dispatch } = method;
  utils.setUpdateBagButtonStatus( { value: false }, { setShowUpdateBag } );

  // Only update if fulfillmentSection have changed
  dispatch( { type: utils.STATE.UPDATE, payload: props, fulfillmentSection, cart, store, sddStore, sddInfo, isFulfillmentChanged, methods: { setIsSubscribed, setSubscriptionFrequency, setPrevLabel, setOriginalModuleState, setShowUpdateBag } } );
};

/**
 * setUpdateBagButtonStatus function for updating the update button state
 * @method
 * @param {object} data Arguments
 * @param {object} methods Methods
 */
export const setUpdateBagButtonStatus = ( data, methods ) => {
  const { fulfillmentType, isSubsOpted, subsFrequency, originalModuleState, cart, store, sddStore, sddInfo, value } = data;
  const { setShowUpdateBag, setIsFulfillmentChanged } = methods;
  const { cartBopisStoreId, cartSddStoreId, cartSddInfo } = cart || {};
  const isFulfillmentTypeChanged = fulfillmentType !== originalModuleState?.type;
  const isSubscriptionOptInChanged = isSubsOpted !== originalModuleState?.isSubscribed;
  const isSubscriptionFrequencyChanged = isSubsOpted && subsFrequency !== originalModuleState?.subscriptionFrequency;
  const isBopisStoreChanged = fulfillmentType === fulfillmentTypes.BOPIS && cartBopisStoreId !== store?.bopisStoreId;
  const isSddStoreInfoChanged = fulfillmentType === fulfillmentTypes.SDD && cartSddStoreId !== sddStore?.sddStoreId && !isSddInfoMatches( cartSddInfo?.address, sddInfo?.sddInfo?.address );
  if( value && setIsFulfillmentChanged ){
    setIsFulfillmentChanged( isFulfillmentTypeChanged );
  }

  if( value &&
    ( isFulfillmentTypeChanged || isSubscriptionOptInChanged || isSubscriptionFrequencyChanged || isBopisStoreChanged || isSddStoreInfoChanged ) ){
    setShowUpdateBag( true );
  }
  else {
    setShowUpdateBag( false );
  }
};

/**
 * Determines the correct bookmark action.
 * If there is only one item in the wishlist, it returns the remove action, otherwise the general wishlist action.
 *
 * @param {object} params - The parameters.
 * @param {boolean} params.hasOneItemInWishList - Indicates if there is only one item in the wishlist.
 * @param {object} params.removeBookmarkAction - The action to remove a bookmark.
 * @param {object} params.wishListAction - The action for the wishlist.
 * @returns {object} The appropriate bookmark action.
 */
export const getRemoveWishListAction = ( data ) => {
  const { hasOneItemInWishList, removeBookmarkAction, wishListAction, isWishListEnabledInPDP } = data;
  return ( hasOneItemInWishList || !isWishListEnabledInPDP ) ? removeBookmarkAction : wishListAction;
};

export default ProductActionsV2;
